/**
 * OnboardingStep3Page — goals and consent (/onboarding/3).
 * Receives userType and profileData from location state; completes onboarding on submit.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import {
  useOnboardingStep3,
  useCurrentUser,
  OnboardingStep3Request,
} from '@/lib/api/auth';
import { getErrorMessage } from '@/lib/api/client';
import OnboardingStep3Goals from '@/components/onboarding/OnboardingStep3Goals';

export default function OnboardingStep3Page() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const userType: 'student' | 'parent' | 'teacher' = location.state?.userType ?? 'student';
  const profileData = location.state?.profileData ?? null;

  const { data: user, isLoading: userLoading } = useCurrentUser();
  const step3Mutation = useOnboardingStep3();

  useEffect(() => {
    if (!userLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, userLoading, navigate]);

  const handleComplete = async (data: OnboardingStep3Request) => {
    try {
      const response = await step3Mutation.mutateAsync({ ...data, profile_data: profileData });
      toast({ title: 'Welcome to Oracy!', description: 'Your account has been set up successfully.' });
      const finalRole = response.user.role || userType;
      if (finalRole === 'teacher' || finalRole === 'admin') {
        navigate('/teacher', { replace: true });
      } else if (finalRole === 'student') {
        navigate('/student', { replace: true });
      } else {
        navigate('/parent', { replace: true });
      }
    } catch (error: unknown) {
      toast({
        title: 'Onboarding Failed',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  if (userLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <OnboardingStep3Goals
      userType={userType}
      onComplete={handleComplete}
      onBack={() => navigate('/onboarding/2', { state: { userType } })}
      isLoading={step3Mutation.isPending}
    />
  );
}
